import { useEffect, useState } from "react";
import { absUrl } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const SIZES = [64, 128, 256, 512];
const RETRY_MS = 1500;

// Fallback when there is no icon or it can't load: the app's initial on the brand tile (never broken-image alt text).
const LetterTile = ({ name, size, px, testId }) => (
  <div className={`${size} metal-red flex shrink-0 select-none items-center justify-center rounded-2xl font-display font-black uppercase text-[var(--gold-bright)]`}
    style={{ fontSize: Math.round(px * 0.45) }} role="img" aria-label={name} data-testid={testId} data-fallback="letter">
    {(name || "?").trim().charAt(0)}
  </div>
);

// WebP first (64–512 px, chosen by the browser from the rendered size), PNG fallback; one retry, then the letter tile.
export const AppIcon = ({ app, size = "h-16 w-16", px = 64, testId }) => {
  const { t } = useI18n();
  const [tries, setTries] = useState(0);
  const [waiting, setWaiting] = useState(false);
  useEffect(() => {
    if (!waiting) return undefined;
    const id = setTimeout(() => {
      setWaiting(false);
      setTries(1);
    }, RETRY_MS);
    return () => clearTimeout(id);
  }, [waiting]);
  if (!app.icon_url || tries > 1) return <LetterTile name={app.name} size={size} px={px} testId={testId} />;
  const variant = (s, fmt) => `${absUrl(app.icon_url)}&size=${s}&fmt=${fmt}${tries ? "&retry=1" : ""}`;
  const srcSet = (fmt) => SIZES.map((s) => `${variant(s, fmt)} ${s}w`).join(", ");
  const onError = () => (tries === 0 ? setWaiting(true) : setTries(2));
  return (
    <picture className="contents" key={tries}>
      <source type="image/webp" srcSet={srcSet("webp")} sizes={`${px}px`} />
      <img
        src={variant(128, "png")}
        srcSet={srcSet("png")}
        sizes={`${px}px`}
        alt={t("apps.iconAlt", { name: app.name })}
        loading="lazy"
        decoding="async"
        width={px}
        height={px}
        onError={onError}
        className={`${size} shrink-0 rounded-2xl object-cover ring-1 ring-[rgba(245,197,66,0.45)] ${waiting ? "invisible" : ""}`}
        data-testid={testId}
      />
    </picture>
  );
};
